/** FE-owned Pulse/Daton URLs for onboarding (Option A + D24). */

import * as Sentry from "@sentry/react";
import config from "@/config";
import { ROUTES } from "@/constants";
import {
  DEFAULT_DATON_CDK_ENABLED_SOURCES,
  normalizeDatonSourceType,
} from "@/constants/datonCdkEnabledSources";
import type { IUserInfo } from "@/services/apis/authApi";
import type { OnboardingWarehouseBlock } from "@/types/onboarding.types";
import { hasMultipleWarehouseCandidates } from "@/utils/onboardingWarehouseCandidates";

export type IqSetupStep = "warehouse" | "sources";

const trimSlash = (base: string) => base.replace(/\/+$/, "");

const DEFAULT_PULSE_APP = "https://insights.sarasanalytics.com";
const DEFAULT_DATON_APP = "https://daton.sarasanalytics.com";

/**
 * `VITE_PRODUCT_PULSE` / `VITE_PRODUCT_DATON` are often login entry URLs (`…/login`).
 * Hub and add-source links need the product app origin (Option A / BFF walk).
 */
export function productAppBase(raw: string | undefined, fallback: string): string {
  const candidate = (raw?.trim() || fallback).trim();
  try {
    const u = new URL(candidate);
    u.pathname = u.pathname.replace(/\/+(login|auth)\/?$/i, "") || "/";
    if (u.pathname === "/") {
      return trimSlash(u.origin);
    }
    return trimSlash(`${u.origin}${u.pathname}`);
  } catch {
    return trimSlash(fallback);
  }
}

function pulseAppBase(): string {
  return productAppBase(config.products.PULSE, DEFAULT_PULSE_APP);
}

function datonAppBase(): string {
  return productAppBase(config.products.DATON, DEFAULT_DATON_APP);
}

function appendIqSetupParams(url: string, step: IqSetupStep): string {
  const u = new URL(url);
  u.searchParams.set("iq_setup", "1");
  u.searchParams.set("iq_setup_step", step);
  // Where Daton / Pulse send the user back to once they finish creating a source or destination.
  u.searchParams.set("iq_return", `${window.location.origin}${ROUTES.CHAT}`);
  return u.toString();
}

/** Legacy BFF path; Pulse has no ``/source/:id`` SPA route. */
const PULSE_LEGACY_MANAGE_PATH = /^\/source\/\d+\/?$/;

/**
 * Daton BigQuery destination (Step 1). Opens the BigQuery configuration page directly:
 * a numeric `VITE_DATON_BIGQUERY_WAREHOUSE_ID` links by id; when unset, Daton's
 * `/u/warehouse/type/BIGQUERY` route resolves the id (ids differ per environment), so the
 * user skips the warehouse listing. An invalid id falls back to the listing.
 */
export function datonWarehouseListingUrl(): string {
  const base = datonAppBase();
  const bigQueryId = config.datonBigQueryWarehouseId?.trim();
  let path = "/u/warehouse/type/BIGQUERY";
  if (bigQueryId) {
    path = /^\d+$/.test(bigQueryId)
      ? `/u/warehouse/${bigQueryId}`
      : "/u/connectors/datawarehouses-listing";
  }
  return appendIqSetupParams(`${base}${path}`, "warehouse");
}

/** Daton destinations list (Settings → Integrations → Manage BigQuery). */
export function datonDestinationsListingUrl(): string {
  return `${datonAppBase()}/u/destinations-list`;
}

/** Daton edit-destination flow (same route as Integrations → Edit warehouse). */
export function datonEditWarehouseUrl(
  datawarehouseId: number | string,
  warehouseId: number | string,
): string {
  return appendIqSetupParams(
    `${datonAppBase()}/u/edit-warehouse/${encodeURIComponent(String(datawarehouseId))}/${encodeURIComponent(String(warehouseId))}`,
    "warehouse",
  );
}

/** Manage warehouse: destinations list when several BQ rows; else edit deep-link or hub. */
export function datonManageWarehouseUrl(
  block?: Pick<
    OnboardingWarehouseBlock,
    "datawarehouseId" | "warehouseId" | "candidates"
  > | null,
): string {
  if (hasMultipleWarehouseCandidates(block?.candidates)) {
    return datonDestinationsListingUrl();
  }
  const templateId = block?.datawarehouseId;
  const instanceId = block?.warehouseId;
  if (
    templateId != null &&
    instanceId != null &&
    String(templateId).trim() !== "" &&
    String(instanceId).trim() !== ""
  ) {
    return datonEditWarehouseUrl(templateId, instanceId);
  }
  return datonDestinationsListingUrl();
}

/** Step 1 / Integrations warehouse CTA — connect, manage, or pick among destinations. */
export function resolveDatonWarehouseOpenUrl(
  block?: OnboardingWarehouseBlock | null,
): string {
  if (hasMultipleWarehouseCandidates(block?.candidates)) {
    return datonDestinationsListingUrl();
  }
  if (block?.connected === true) {
    return datonManageWarehouseUrl(block);
  }
  return datonWarehouseListingUrl();
}

/** Resolve BFF relative manage paths to a full product URL. */
export function resolveProductManageUrl(
  product: "pulse" | "daton",
  manageUrl: string,
): string {
  const trimmed = manageUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const base = product === "pulse" ? pulseAppBase() : datonAppBase();
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
}

/**
 * Connected-chip manage links from the BFF include D24 when opened from iQ onboarding.
 * Maps invalid Pulse ``/source/{id}`` paths to the sources hub.
 */
export function resolveOnboardingManageUrl(
  product: "pulse" | "daton",
  manageUrl: string,
  step: IqSetupStep = "sources",
): string {
  const trimmed = manageUrl.trim();
  if (product === "pulse") {
    if (PULSE_LEGACY_MANAGE_PATH.test(trimmed)) {
      return pulseSourcesHubUrl();
    }
    return appendIqSetupParams(resolveProductManageUrl("pulse", trimmed), step);
  }
  const absolute = resolveProductManageUrl("daton", trimmed);
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (
    path.startsWith("/integrations/") ||
    path.startsWith("/u/integrations") ||
    path.startsWith("/u/integration-details/") ||
    path.startsWith("/u/edit-integration-new/") ||
    path.startsWith("/u/edit-studio-source/")
  ) {
    return appendIqSetupParams(absolute, step);
  }
  return absolute;
}

/** Pulse sources hub (Step 2). */
export function pulseSourcesHubUrl(): string {
  return appendIqSetupParams(`${pulseAppBase()}/sources`, "sources");
}

/** Daton datasources hub (Step 2 setup wizard). */
export function datonSourcesHubUrl(): string {
  return appendIqSetupParams(
    `${datonAppBase()}/u/connectors/datasources-listing`,
    "sources",
  );
}

/** Daton integrations list (Settings → Integrations → Manage sources). */
export function datonIntegrationsListUrl(): string {
  return `${datonAppBase()}/u/integrations-list`;
}

/** Daton integration details (same route as integrations-list action menu). */
export function datonIntegrationDetailsUrl(
  integrationName: string,
  sourceId: number | string,
): string {
  const name = encodeURIComponent(integrationName.trim());
  return `/u/integration-details/${name}/${encodeURIComponent(String(sourceId))}`;
}

/** Daton edit-integration-new flow (non-legacy CDK path segment). */
export function datonEditIntegrationUrl(
  datasourceId: number | string,
  integrationName: string,
  sourceId: number | string,
): string {
  return `/u/edit-integration-new/${encodeURIComponent(String(datasourceId))}/${encodeURIComponent(integrationName.trim())}/${encodeURIComponent(String(sourceId))}`;
}

/** Canonical L5 types that differ from Pulse ``init-details/by-type`` path segments. */
const PULSE_ADD_SOURCE_ROUTE_ALIASES: Record<string, string> = {
  AMAZON_SELLER_CENTRAL: "AMAZONSELLER",
  AMAZON_SELLER: "AMAZONSELLER",
};

/** Resolve the type segment Pulse expects in ``/add-source/{type}`` URLs. */
export function pulseAddSourceRouteType(
  canonicalType: string,
  pulseType?: string | null,
): string {
  const fromBff = pulseType?.trim();
  if (fromBff) {
    return fromBff;
  }
  const key = canonicalType.trim().toUpperCase().replace(/-/g, "_");
  return PULSE_ADD_SOURCE_ROUTE_ALIASES[key] ?? canonicalType;
}

/** Per-type add-source deep link. */
export function pulseAddSourceUrl(
  type: string,
  pulseType?: string | null,
): string {
  const routeType = pulseAddSourceRouteType(type, pulseType);
  return appendIqSetupParams(
    `${pulseAppBase()}/add-source/${encodeURIComponent(routeType)}`,
    "sources",
  );
}

function datonCdkEnabledTypes(): Set<string> {
  const configured = config.datonCdkEnabledSources;
  const list = configured.length > 0 ? configured : DEFAULT_DATON_CDK_ENABLED_SOURCES;
  return new Set(list.map(normalizeDatonSourceType));
}

/**
 * Daton path segment for add-source, aligned with ``datasources-listing``:
 * types in ``CDK_ENABLED_SOURCES`` → ``add-source-new``; other CDK rows → studio.
 */
export function datonAddSourcePath(type: string, cdkSource?: boolean | null): string {
  const encoded = encodeURIComponent(type);
  const inCdkEnabledList = datonCdkEnabledTypes().has(normalizeDatonSourceType(type));
  if (inCdkEnabledList || !cdkSource) {
    return `/u/add-source-new/${encoded}`;
  }
  return `/u/add-studio-source/${encoded}`;
}

/**
 * Per-type Daton add-source deep link (matches Daton catalog routing).
 *
 * @param type - Canonical connector type.
 * @param cdkSource - True when the upstream catalog marks the row as CDK.
 * @returns Full Daton add-source URL with iQ setup query params.
 */
export function datonAddSourceUrl(type: string, cdkSource?: boolean | null): string {
  return appendIqSetupParams(`${datonAppBase()}${datonAddSourcePath(type, cdkSource)}`, "sources");
}

/** Products whose tabs iQ reuses instead of opening a new one on every click. */
export type ProductWindow = "pulse" | "daton";

/** Session material for cross-product /auth handoff (same contract as global apps). */
export type ProductHandoffSession = {
  firebaseToken?: string | null;
  user?: IUserInfo | null;
};

/** Minimal ``user`` query payload Pulse/Daton ``/auth`` reads (verify + custom token name). */
export type ProductHandoffUserPayload = {
  userId?: string | number;
  email: string;
  firstName?: string;
  lastName?: string;
};

/**
 * Build the ``user`` object for cross-app ``/auth`` — no company secrets or IQ profile fields.
 *
 * Pulse ``auth.component`` and Daton ``auth.component`` use ``email``, ``firstName``, ``lastName``,
 * and ``userId`` only (see their handoff specs).
 */
export function buildProductHandoffUser(user: IUserInfo): ProductHandoffUserPayload {
  const userId = user.userId ?? (user as { user_id?: string | number }).user_id;
  const payload: ProductHandoffUserPayload = { email: user.email! };
  if (userId != null && String(userId).trim() !== "") {
    payload.userId = userId;
  }
  if (user.firstName?.trim()) {
    payload.firstName = user.firstName.trim();
  }
  if (user.lastName?.trim()) {
    payload.lastName = user.lastName.trim();
  }
  return payload;
}

/**
 * JSON with every non-ASCII character written as a ``\uXXXX`` escape. ``btoa`` throws on
 * characters above U+00FF (a "’" or "™" in a name), and Pulse/Daton read the param with
 * ``JSON.parse(atob(user))``, which turns the escapes back into the same text.
 */
function asciiJson(value: unknown): string {
  return JSON.stringify(value).replace(
    /[\u0080-￿]/g,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`,
  );
}

/**
 * Route through product ``/auth`` with ``token`` + ``user`` so Daton/Pulse establish
 * ``saras_auth_key`` before guarded ``/u/…`` routes (otherwise Daton AuthGuard → ``/logout``).
 */
export function withProductAuthHandoff(
  product: ProductWindow,
  destinationUrl: string,
  session?: ProductHandoffSession | null,
): string {
  const token = session?.firebaseToken?.trim();
  const user = session?.user;
  if (!token || !user?.email) {
    return destinationUrl;
  }
  const handoffUser = buildProductHandoffUser(user);
  const base = product === "pulse" ? pulseAppBase() : datonAppBase();
  const authUrl = new URL(`${base}/auth`);
  authUrl.searchParams.set("token", btoa(token));
  authUrl.searchParams.set("user", btoa(asciiJson(handoffUser)));
  authUrl.searchParams.set("redirectTo", destinationUrl);
  return authUrl.href;
}

/** Non-secret facts about a handoff token, for diagnosing expired-token handoffs. */
export type HandoffTokenInfo = {
  present: boolean;
  /** Seconds since the token was issued (`iat`). */
  ageSec?: number;
  /** Seconds until `exp`; negative once expired. */
  expiresInSec?: number;
};

/**
 * Describe a JWT's age and remaining life without exposing it. A missing token is
 * `{ present: false }`; one that cannot be decoded is `{ present: true }` only.
 */
export function describeHandoffToken(token?: string | null): HandoffTokenInfo {
  if (!token) {
    return { present: false };
  }
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const { iat, exp } = JSON.parse(json) as { iat?: number; exp?: number };
    const now = Math.floor(Date.now() / 1000);
    return {
      present: true,
      ...(typeof iat === "number" ? { ageSec: now - iat } : {}),
      ...(typeof exp === "number" ? { expiresInSec: exp - now } : {}),
    };
  } catch {
    return { present: true };
  }
}

/** Short id that joins iQ's handoff log line with the product's `[iq-handoff]` trace. */
function newHandoffTraceId(): string {
  return `iq-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Handoff when possible, then open in the product tab (see ``openExternal``).
 *
 * Adds an ``iq_trace`` id to the destination (read by Daton's handoff trace, also from
 * inside ``redirectTo``) and leaves one ``iq-handoff`` Sentry breadcrumb with token
 * age/expiry (never the token itself).
 */
export function openOnboardingProduct(
  destinationUrl: string,
  product: ProductWindow,
  session?: ProductHandoffSession | null,
): boolean {
  const traceId = newHandoffTraceId();
  let traced = destinationUrl;
  try {
    const u = new URL(destinationUrl);
    u.searchParams.set("iq_trace", traceId);
    traced = u.toString();
  } catch {
    // Not an absolute URL: open it as given, untraced.
  }
  Sentry.addBreadcrumb({
    category: "iq-handoff",
    message: "open",
    level: "info",
    data: {
      traceId,
      product,
      token: describeHandoffToken(session?.firebaseToken),
    },
  });
  return openExternal(withProductAuthHandoff(product, traced, session), product);
}

/** Browser window names, so a repeat click navigates the tab already open for that product. */
const PRODUCT_WINDOW_NAME: Record<ProductWindow, string> = {
  pulse: "iq-open-pulse",
  daton: "iq-open-daton",
};

/**
 * Whether iQ should reuse one named browser tab per product (D24 handoff UX).
 *
 * Saras first-party products (Daton and Pulse) reuse their named window so repeat clicks
 * focus the tab and `window.opener` is preserved for returning to iQ.
 */
export function shouldReuseOnboardingProductWindow(): boolean {
  return true;
}

/**
 * Open an external page in a new tab.
 *
 * With a `product`, the tab gets a fixed window name: if a Pulse or Daton tab opened
 * this way is still open, the browser navigates and focuses it instead of adding another.
 * That leaves `window.opener` intact (`noopener` would strip the name and force a new tab),
 * which is acceptable for Saras's own products and required so "Continue in iQ" can close
 * the tab and return focus to iQ. Without a `product`, it stays an isolated new tab with
 * `opener = null`.
 *
 * @returns False when the browser blocked the window.
 */
export function openExternal(url: string, product?: ProductWindow): boolean {
  try {
    if (product) {
      const win = window.open(url, PRODUCT_WINDOW_NAME[product]);
      win?.focus();
      return win != null;
    }
    const win = window.open(url, "_blank");
    if (win) {
      win.opener = null;
    }
    return win != null;
  } catch {
    return false;
  }
}

/** Infer product from a BFF relative ``manageUrl`` when ``connectedVia`` is absent. */
export function productForManageUrl(manageUrl: string): ProductWindow | null {
  const path = manageUrl.trim();
  if (/^\/add-source\//.test(path) || path === "/sources" || /^\/source\/\d+/.test(path)) {
    return "pulse";
  }
  if (
    path.startsWith("/integrations/") ||
    path.startsWith("/u/integration-details/") ||
    path.startsWith("/u/integrations")
  ) {
    return "daton";
  }
  return null;
}

/**
 * Determine which product (Pulse or Daton) owns a connected source chip.
 *
 * @param chip - Source chip with `connectVia`, optional `connectedVia`, and `manageUrl`.
 * @returns The product window (`"pulse"` or `"daton"`) where the connection is managed.
 */
export function connectedProductForChip(chip: {
  connectVia: ProductWindow;
  connectedVia?: ProductWindow | null;
  manageUrl?: string | null;
}): ProductWindow {
  if (chip.connectedVia === "pulse" || chip.connectedVia === "daton") {
    return chip.connectedVia;
  }
  const fromUrl = chip.manageUrl?.trim()
    ? productForManageUrl(chip.manageUrl)
    : null;
  return fromUrl ?? chip.connectVia;
}

/**
 * Whether a BFF ``manageUrl`` is a real per-source deep link: Pulse
 * ``/add-source/:type?editSourceId=<id>`` (edit mode) or Daton
 * ``/u/integration-details/:name/:sourceId``. Hub paths such as ``/sources`` are not.
 */
function isSourceDeepLink(product: ProductWindow, manageUrl: string): boolean {
  if (product === "pulse") {
    return /^\/add-source\/[^/?]+\?(?:.*&)?editSourceId=\d+/.test(manageUrl);
  }
  return (
    manageUrl.startsWith("/u/integration-details/") ||
    /^\/integrations\/\d+/.test(manageUrl.trim())
  );
}

export type OpenUnifiedSourceChipOptions = {
  /** Add-source picker (Integrations dialog): always open add / studio / add-source-new. */
  preferAddSource?: boolean;
};

/**
 * Navigate to Pulse/Daton for a unified catalog chip (setup + integrations).
 *
 * - Connected chip with a per-source deep link: opens that source (Pulse edit mode,
 *   Daton source page), so the user does not land on a hub or a blank "add" form.
 * - Connected chip without one: the add-source page of the product that holds the
 *   connection ({@link connectedProductForChip}), never the other product's form.
 * - Otherwise: that connector's add-source page (Pulse ``/add-source/:type``, Daton
 *   ``/u/add-source-new/:type`` or ``/u/add-studio-source/:type`` for CDK sources).
 *
 * @returns False when the browser blocked the new tab, so the caller can say so.
 */
export function openUnifiedSourceChip(
  openOnboardingProduct: (url: string, product: ProductWindow) => boolean,
  chip: {
    type: string;
    connectVia: ProductWindow;
    connectedVia?: ProductWindow | null;
    connected?: boolean | null;
    manageUrl?: string | null;
    cdkSource?: boolean | null;
    pulseType?: string | null;
  },
  options?: OpenUnifiedSourceChipOptions,
): boolean {
  const managing = !options?.preferAddSource && chip.connected === true;
  const manageProduct = connectedProductForChip(chip);
  const manage = chip.manageUrl?.trim();
  if (managing && manage && isSourceDeepLink(manageProduct, manage)) {
    return openOnboardingProduct(
      resolveOnboardingManageUrl(manageProduct, manage),
      manageProduct,
    );
  }
  const product = managing ? manageProduct : chip.connectVia;
  const url =
    product === "pulse"
      ? pulseAddSourceUrl(chip.type, chip.pulseType)
      : datonAddSourceUrl(chip.type, chip.cdkSource);
  return openOnboardingProduct(url, product);
}
